import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { useDesignStore } from '../../store/designStore';
import type { NameNumber } from '@customedge/shared';
import { ToolSection } from './shared/ToolSection';

interface NameNumberToolProps {
  canvas: import('../../types/fabric').FabricCanvas | null;
}

const emptyPlayer = (): NameNumber => ({ id: crypto.randomUUID(), name: '', number: '' });

export function NameNumberTool({ canvas: _canvas }: NameNumberToolProps) {
  const { t } = useTranslation();
  const { nameNumbers, setNameNumbers } = useDesignStore();
  const [csvError, setCsvError] = useState('');

  const addRow = () => setNameNumbers([...nameNumbers, emptyPlayer()]);

  const updateRow = (id: string, field: keyof NameNumber, value: string) => {
    setNameNumbers(nameNumbers.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removeRow = (id: string) => {
    setNameNumbers(nameNumbers.filter(p => p.id !== id));
  };

  const importCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string;
        const lines = text.trim().split(/\r?\n/);
        // Support header row: name,number (or number,name)
        const isHeader = /name|number/i.test(lines[0] ?? '');
        const dataLines = isHeader ? lines.slice(1) : lines;
        const parsed: NameNumber[] = dataLines
          .filter(l => l.trim())
          .map(l => {
            const [a, b] = l.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
            return { id: crypto.randomUUID(), name: a ?? '', number: b ?? '' };
          });
        if (!parsed.length) throw new Error('No data rows found');
        setNameNumbers(parsed);
      } catch (err: any) {
        setCsvError(t('nameNumber.csvError', { error: err.message ?? String(err) }));
      }
      // Reset file input so same file can be re-imported
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const exportCSV = () => {
    const rows = ['name,number', ...nameNumbers.map(p => `${csvEsc(p.name)},${csvEsc(p.number)}`)];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'players.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('nameNumber.sectionPlayers')} divider={false}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
          {t('nameNumber.description', { nameTag: '{{name}}', numberTag: '{{number}}' })}
        </p>

        {/* Table */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2, #f1f5f9)' }}>
                <th style={thStyle}>#</th>
                <th style={thStyle}>{t('nameNumber.name')}</th>
                <th style={thStyle}>{t('nameNumber.number')}</th>
                <th style={{ ...thStyle, width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {nameNumbers.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                    {t('nameNumber.noPlayers')}
                  </td>
                </tr>
              )}
              {nameNumbers.map((p, idx) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...tdStyle, color: 'var(--text-muted)', textAlign: 'center', width: 28 }}>
                    {idx + 1}
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={p.name}
                      onChange={e => updateRow(p.id, 'name', e.target.value)}
                      placeholder={t('nameNumber.name')}
                      style={inputStyle}
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      value={p.number}
                      onChange={e => updateRow(p.id, 'number', e.target.value)}
                      placeholder={t('nameNumber.number')}
                      style={{ ...inputStyle, width: 60 }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => removeRow(p.id)}
                      style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px 4px' }}
                      title={t('nameNumber.delete')}
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button onClick={addRow} className="btn btn-secondary" style={{ fontSize: 12, width: '100%' }}>
          {t('nameNumber.addPlayer')}
        </button>

        {nameNumbers.length > 0 && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
            {t('nameNumber.playerCount', { count: nameNumbers.length })}
          </p>
        )}
      </ToolSection>

      <ToolSection title={t('nameNumber.sectionImportExport')}>
        <div style={{ display: 'flex', gap: 6 }}>
          <label style={{ flex: 1, cursor: 'pointer' }}>
            <span className="btn btn-secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center' }}>
              {t('nameNumber.importCSV')}
            </span>
            <input type="file" accept=".csv,text/csv" onChange={importCSV} style={{ display: 'none' }} />
          </label>
          <button
            onClick={exportCSV}
            disabled={!nameNumbers.length}
            className="btn btn-secondary"
            style={{ flex: 1, fontSize: 12 }}
          >
            {t('nameNumber.exportCSV')}
          </button>
        </div>

        {csvError && (
          <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>{csvError}</p>
        )}
      </ToolSection>
    </div>
  );
}

function csvEsc(v: string): string {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

const thStyle: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)',
};
const tdStyle: React.CSSProperties = {
  padding: '4px 6px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 3,
  fontSize: 12, background: 'transparent',
};
